import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs,
  getDoc,
  setDoc,
  query,
  where,
  orderBy,
  onSnapshot
} from 'firebase/firestore';
import { db } from './firebase';
import { toEasternISOString } from './dateHelper';
// notifyNewAppointment is no longer called client-side; Cloud Function handles it
// import { notifyNewAppointment } from './pushNotificationService';

// ==================== ADMIN CHECK ====================

// Check if a user is an admin by their Firebase Auth UID
export const checkIsAdmin = async (uid) => {
  if (!uid) return false;
  try {
    const adminDoc = await getDoc(doc(db, 'admins', uid));
    return adminDoc.exists();
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};

// Check if a user is the Owner (Super Admin)
export const checkIsOwner = async (uid) => {
  if (!uid) return false;
  try {
    const ownerDoc = await getDoc(doc(db, 'owners', uid));
    return ownerDoc.exists();
  } catch (error) {
    console.error('Error checking owner status:', error);
    return false;
  }
};

// Collections
const APPOINTMENTS = 'appointments';
const PREPARERS = 'preparers';
const AVAILABILITY = 'availability';
const COMPANIES = 'companies';

// ==================== COMPANIES (For Owner) ====================

export const addCompany = async (companyData) => {
  try {
    // Use provided slug or generate from company name
    const slug = companyData.slug || companyData.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    
    // Check if slug already exists
    const existingCompany = await getCompanyBySlug(slug);
    if (existingCompany) {
      throw new Error('A company with this URL slug already exists. Please use a different slug.');
    }
    
    const docRef = await addDoc(collection(db, COMPANIES), {
      ...companyData,
      slug,
      isActive: true,
      isPublished: false,
      createdAt: toEasternISOString()
    });
    return { id: docRef.id, ...companyData, slug };
  } catch (error) {
    console.error('Error adding company:', error);
    throw error;
  }
};

export const getCompanies = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, COMPANIES));
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting companies:', error);
    throw error;
  }
};

export const getCompanyBySlug = async (slug) => {
  try {
    const q = query(collection(db, COMPANIES), where('slug', '==', slug));
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) return null;
    const docSnap = querySnapshot.docs[0];
    return { id: docSnap.id, ...docSnap.data() };
  } catch (error) {
    console.error('Error getting company by slug:', error);
    return null;
  }
};

// Subscribe to company data changes (real-time updates)
export const subscribeToCompanyBySlug = (slug, callback) => {
  try {
    const q = query(collection(db, COMPANIES), where('slug', '==', slug));
    return onSnapshot(q, (snapshot) => {
      if (snapshot.empty) {
        callback(null);
      } else {
        const docSnap = snapshot.docs[0];
        callback({ id: docSnap.id, ...docSnap.data() });
      }
    });
  } catch (error) {
    console.error('Error subscribing to company:', error);
    return () => {};
  }
};

export const getCompanyById = async (companyId) => {
  try {
    const docRef = doc(db, COMPANIES, companyId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting company by ID:', error);
    return null;
  }
};

export const updateCompany = async (id, data) => {
  try {
    const docRef = doc(db, COMPANIES, id);
    await updateDoc(docRef, {
      ...data,
      updatedAt: toEasternISOString()
    });
    return { id, ...data };
  } catch (error) {
    console.error('Error updating company:', error);
    throw error;
  }
};

export const deleteCompany = async (id) => {
  try {
    await deleteDoc(doc(db, COMPANIES, id));
    return id;
  } catch (error) {
    console.error('Error deleting company:', error);
    throw error;
  }
};

export const toggleCompanyActive = async (id, isActive) => {
  try {
    const docRef = doc(db, COMPANIES, id);
    await updateDoc(docRef, { isActive, updatedAt: toEasternISOString() });
    return { id, isActive };
  } catch (error) {
    console.error('Error toggling company:', error);
    throw error;
  }
};

export const toggleCompanyPublished = async (id, isPublished) => {
  try {
    const docRef = doc(db, COMPANIES, id);
    await updateDoc(docRef, { isPublished, updatedAt: toEasternISOString() });
    return { id, isPublished };
  } catch (error) {
    console.error('Error toggling company published status:', error);
    throw error;
  }
};

// Subscribe to companies for real-time updates
export const subscribeToCompanies = (callback) => {
  return onSnapshot(collection(db, COMPANIES), (snapshot) => {
    const companies = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(companies);
  });
};

// Get company by admin credentials (username)
export const getCompanyByAdminUsername = async (username) => {
  try {
    const q = query(collection(db, COMPANIES), where('adminUsername', '==', username));
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) return null;
    const docSnap = querySnapshot.docs[0];
    return { id: docSnap.id, ...docSnap.data() };
  } catch (error) {
    console.error('Error getting company by admin username:', error);
    return null;
  }
};

// Verify company admin credentials
export const verifyCompanyAdmin = async (username, password) => {
  try {
    const company = await getCompanyByAdminUsername(username);
    if (company && company.adminPassword === password) {
      return company;
    }
    return null;
  } catch (error) {
    console.error('Error verifying company admin:', error);
    return null;
  }
};

// ==================== APPOINTMENTS ====================

export const addAppointment = async (appointmentData) => {
  try {
    console.log('📅 Adding appointment:', {
      preparer: appointmentData.preparerId || appointmentData.preparer,
      date: appointmentData.date,
      time: appointmentData.time,
      client: appointmentData.fullName || appointmentData.clientName,
      companyId: appointmentData.companyId
    });
    
    const docRef = await addDoc(collection(db, APPOINTMENTS), {
      ...appointmentData,
      createdAt: toEasternISOString(),
      confirmed: false,
      status: appointmentData.status || 'pending'
    });
    
    console.log('✅ Appointment saved with ID:', docRef.id);
    
    const savedAppointment = { id: docRef.id, ...appointmentData, status: appointmentData.status || 'pending' };
    
    // NOTE: Push notification is handled by the Firebase Cloud Function (sendAppointmentNotification)
    // which triggers on Firestore document creation. No need to also send from the client side,
    // as that was causing duplicate notifications.
    
    return savedAppointment;
  } catch (error) {
    console.error('❌ Error adding appointment:', error);
    throw error;
  }
};

export const getAppointments = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, APPOINTMENTS));
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting appointments:', error);
    throw error;
  }
};

export const getAppointmentsByCompany = async (companyId) => {
  try {
    const q = query(collection(db, APPOINTMENTS), where('companyId', '==', companyId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting appointments by company:', error);
    throw error;
  }
};

export const updateAppointment = async (id, data) => {
  try {
    const docRef = doc(db, APPOINTMENTS, id);
    await updateDoc(docRef, data);
    return { id, ...data };
  } catch (error) {
    console.error('Error updating appointment:', error);
    throw error;
  }
};

export const deleteAppointment = async (id) => {
  try {
    await deleteDoc(doc(db, APPOINTMENTS, id));
    return id;
  } catch (error) {
    console.error('Error deleting appointment:', error);
    throw error;
  }
};

export const confirmAppointment = async (id) => {
  try {
    // Ensure id is a valid string
    if (!id || typeof id !== 'string') {
      console.error('Invalid appointment ID:', id, typeof id);
      throw new Error('Invalid appointment ID - must be a non-empty string');
    }
    const docRef = doc(db, APPOINTMENTS, String(id));
    await updateDoc(docRef, { 
      confirmed: true,
      confirmedAt: toEasternISOString()
    });
    return id;
  } catch (error) {
    console.error('Error confirming appointment:', error);
    throw error;
  }
};

// Real-time listener for appointments
export const subscribeToAppointments = (callback, companyId = null) => {
  let q;
  if (companyId) {
    q = query(
      collection(db, APPOINTMENTS), 
      where('companyId', '==', companyId),
      orderBy('createdAt', 'desc')
    );
  } else {
    q = query(collection(db, APPOINTMENTS), orderBy('createdAt', 'desc'));
  }
  return onSnapshot(q, (snapshot) => {
    const appointments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    console.log('📋 Appointments loaded:', appointments.length, 'appointments');
    callback(appointments);
  }, (error) => {
    console.error('❌ Error listening to appointments:', error);
    // If it's an index error, log the helpful link
    if (error.code === 'failed-precondition' || error.message?.includes('index')) {
      console.error('🔗 You may need to create a Firestore index. Check the link above in the error message.');
    }
    // Return empty array to avoid breaking the UI
    callback([]);
  });
};

// ==================== PREPARERS ====================

export const addPreparer = async (preparerData) => {
  try {
    const docRef = await addDoc(collection(db, PREPARERS), {
      ...preparerData,
      active: true,
      published: true,
      createdAt: toEasternISOString()
    });
    return { id: docRef.id, ...preparerData };
  } catch (error) {
    console.error('Error adding preparer:', error);
    throw error;
  }
};

export const getPreparers = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, PREPARERS));
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting preparers:', error);
    throw error;
  }
};

export const getPreparersByCompany = async (companyId) => {
  try {
    const q = query(collection(db, PREPARERS), where('companyId', '==', companyId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting preparers by company:', error);
    throw error;
  }
};

export const getPreparerById = async (preparerId) => {
  try {
    const docRef = doc(db, PREPARERS, preparerId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting preparer by ID:', error);
    return null;
  }
};

export const updatePreparer = async (id, data) => {
  try {
    const docRef = doc(db, PREPARERS, id);
    await updateDoc(docRef, data);
    return { id, ...data };
  } catch (error) {
    console.error('Error updating preparer:', error);
    console.error('Error details:', error.code, error.message);
    throw error;
  }
};

export const deletePreparer = async (id) => {
  try {
    await deleteDoc(doc(db, PREPARERS, id));
    return id;
  } catch (error) {
    console.error('Error deleting preparer:', error);
    throw error;
  }
};

export const togglePreparerActive = async (id, active) => {
  try {
    const docRef = doc(db, PREPARERS, id);
    await updateDoc(docRef, { active });
    return { id, active };
  } catch (error) {
    console.error('Error toggling preparer:', error);
    throw error;
  }
};

// Real-time listener for preparers
export const subscribeToPreparers = (callback, companyId = null) => {
  if (companyId) {
    const q = query(collection(db, PREPARERS), where('companyId', '==', companyId));
    return onSnapshot(q, (snapshot) => {
      const preparers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      callback(preparers);
    });
  }
  return onSnapshot(collection(db, PREPARERS), (snapshot) => {
    const preparers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(preparers);
  });
};

// ==================== AVAILABILITY ====================

export const setAvailability = async (preparerName, date, timeSlots, companyId = null) => {
  try {
    const availabilityId = `${preparerName}_${date}`;
    const docRef = doc(db, AVAILABILITY, availabilityId);
    
    const data = {
      preparerName,
      date,
      timeSlots,
      updatedAt: toEasternISOString()
    };
    
    if (companyId) {
      data.companyId = companyId;
    }
    
    await setDoc(docRef, data, { merge: true });
    
    return { preparerName, date, timeSlots };
  } catch (error) {
    console.error('Error setting availability:', error);
    throw error;
  }
};

export const getAvailability = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, AVAILABILITY));
    const availability = {};
    querySnapshot.docs.forEach(doc => {
      const data = doc.data();
      const key = `${data.preparerName}_${data.date}`;
      availability[key] = data.timeSlots;
    });
    return availability;
  } catch (error) {
    console.error('Error getting availability:', error);
    throw error;
  }
};

export const getPreparerAvailability = async (preparerName) => {
  try {
    const q = query(collection(db, AVAILABILITY), where('preparerName', '==', preparerName));
    const querySnapshot = await getDocs(q);
    const availability = {};
    querySnapshot.docs.forEach(doc => {
      const data = doc.data();
      const key = `${data.preparerName}_${data.date}`;
      availability[key] = data.timeSlots;
    });
    return availability;
  } catch (error) {
    console.error('Error getting preparer availability:', error);
    throw error;
  }
};

// Real-time listener for availability
export const subscribeToAvailability = (callback, companyId = null, preparerName = null) => {
  // If we have a specific preparer name, also get their availability
  // This handles cases where availability was saved without companyId
  return onSnapshot(collection(db, AVAILABILITY), (snapshot) => {
    const availability = {};
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      // Include if: no companyId filter, OR matches companyId, OR matches preparerName
      const matchesCompany = !companyId || data.companyId === companyId;
      const matchesPreparer = preparerName && data.preparerName === preparerName;
      
      if (matchesCompany || matchesPreparer || !data.companyId) {
        const key = `${data.preparerName}_${data.date}`;
        availability[key] = data.timeSlots;
      }
    });
    callback(availability);
  });
};

// ==================== COMPANY REQUESTS (For Sales Portal) ====================

const COMPANY_REQUESTS = 'companyRequests';

// Add a new company request from the sales portal
export const addCompanyRequest = async (requestData) => {
  try {
    const docRef = await addDoc(collection(db, COMPANY_REQUESTS), {
      ...requestData,
      status: 'pending', // pending, approved, rejected
      createdAt: toEasternISOString(),
      updatedAt: toEasternISOString()
    });
    return { id: docRef.id, ...requestData, status: 'pending' };
  } catch (error) {
    console.error('Error adding company request:', error);
    throw error;
  }
};

// Get all company requests (for owner)
export const getCompanyRequests = async () => {
  try {
    const q = query(collection(db, COMPANY_REQUESTS), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting company requests:', error);
    throw error;
  }
};

// Subscribe to company requests (real-time)
export const subscribeToCompanyRequests = (callback) => {
  const q = query(collection(db, COMPANY_REQUESTS), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snapshot) => {
    const requests = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(requests);
  });
};

// Update company request status
export const updateCompanyRequest = async (requestId, updates) => {
  try {
    const requestRef = doc(db, COMPANY_REQUESTS, requestId);
    await updateDoc(requestRef, {
      ...updates,
      updatedAt: toEasternISOString()
    });
    return true;
  } catch (error) {
    console.error('Error updating company request:', error);
    throw error;
  }
};

// Delete company request
export const deleteCompanyRequest = async (requestId) => {
  try {
    await deleteDoc(doc(db, COMPANY_REQUESTS, requestId));
    return true;
  } catch (error) {
    console.error('Error deleting company request:', error);
    throw error;
  }
};

// ==================== CONSENT DOCUMENTS ====================

const CONSENT_REQUESTS = 'consentRequests';
const SIGNED_CONSENTS = 'signedConsents';

// Helper to remove undefined values from object (Firebase doesn't accept undefined)
const cleanObject = (obj) => {
  const cleaned = {};
  Object.keys(obj).forEach(key => {
    if (obj[key] !== undefined) {
      cleaned[key] = obj[key];
    }
  });
  return cleaned;
};

// Create a new consent request (sent to taxpayer)
export const createConsentRequest = async (consentData) => {
  try {
    // Clean the data - remove undefined values and ensure all fields have values
    const cleanedData = cleanObject({
      taxpayerName: consentData.taxpayerName || '',
      taxpayerEmail: consentData.taxpayerEmail || '',
      taxpayerPhone: consentData.taxpayerPhone || '',
      taxYear: consentData.taxYear || new Date().getFullYear() - 1,
      preparerId: consentData.preparerId || '',
      preparerName: consentData.preparerName || '',
      companyId: consentData.companyId || '',
      companyName: consentData.companyName || 'Tax Services',
      status: 'pending',
      createdAt: toEasternISOString(),
      expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
    });
    
    console.log('Cleaned consent data:', cleanedData);
    
    const docRef = await addDoc(collection(db, CONSENT_REQUESTS), cleanedData);
    return { id: docRef.id, ...cleanedData };
  } catch (error) {
    console.error('Error creating consent request:', error);
    throw error;
  }
};

// Get consent request by ID
export const getConsentRequest = async (requestId) => {
  try {
    const docRef = doc(db, CONSENT_REQUESTS, requestId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting consent request:', error);
    return null;
  }
};

// Submit signed consent
export const submitSignedConsent = async (requestId, signedData) => {
  try {
    // Get the original request
    const request = await getConsentRequest(requestId);
    if (!request) {
      throw new Error('Consent request not found');
    }

    // Create signed consent document
    const signedConsentRef = await addDoc(collection(db, SIGNED_CONSENTS), {
      requestId,
      companyId: request.companyId,
      preparerId: request.preparerId,
      preparerName: request.preparerName,
      ...signedData,
      status: 'active',
      signedAt: toEasternISOString()
    });

    // Update the original request status
    const requestRef = doc(db, CONSENT_REQUESTS, requestId);
    await updateDoc(requestRef, {
      status: 'signed',
      signedConsentId: signedConsentRef.id,
      signedAt: toEasternISOString()
    });

    return { id: signedConsentRef.id, requestId };
  } catch (error) {
    console.error('Error submitting signed consent:', error);
    throw error;
  }
};

// Submit signed consent directly (for direct access mode without request ID)
export const submitSignedConsentDirect = async (signedData, companyId, preparerId, preparerName) => {
  try {
    // Create signed consent document directly
    const signedConsentRef = await addDoc(collection(db, SIGNED_CONSENTS), {
      requestId: null, // No request - direct access
      companyId: companyId || null,
      preparerId: preparerId || null,
      preparerName: preparerName || 'Unknown',
      ...signedData,
      status: 'active',
      signedAt: toEasternISOString(),
      directSubmission: true // Mark as direct submission
    });

    return { id: signedConsentRef.id };
  } catch (error) {
    console.error('Error submitting signed consent (direct):', error);
    throw error;
  }
};

// Get consent requests by preparer
export const getConsentRequestsByPreparer = async (preparerId) => {
  try {
    const q = query(
      collection(db, CONSENT_REQUESTS),
      where('preparerId', '==', preparerId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting consent requests by preparer:', error);
    return [];
  }
};

// Get signed consents by preparer
export const getSignedConsentsByPreparer = async (preparerId) => {
  try {
    const q = query(
      collection(db, SIGNED_CONSENTS),
      where('preparerId', '==', preparerId),
      orderBy('signedAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting signed consents by preparer:', error);
    return [];
  }
};

// Get all signed consents for company (admin view)
export const getSignedConsentsByCompany = async (companyId) => {
  try {
    const q = query(
      collection(db, SIGNED_CONSENTS),
      where('companyId', '==', companyId),
      orderBy('signedAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting signed consents by company:', error);
    return [];
  }
};

// Get signed consent by ID
export const getSignedConsentById = async (consentId) => {
  try {
    const docRef = doc(db, SIGNED_CONSENTS, consentId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting signed consent:', error);
    return null;
  }
};

// Subscribe to consent requests (real-time) for preparer - only pending ones
export const subscribeToConsentRequests = (preparerId, callback) => {
  const q = query(
    collection(db, CONSENT_REQUESTS),
    where('preparerId', '==', preparerId),
    where('status', '==', 'pending'),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(q, (snapshot) => {
    const requests = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(requests);
  });
};

// Subscribe to signed consents (real-time) for preparer
export const subscribeToSignedConsents = (preparerId, callback) => {
  const q = query(
    collection(db, SIGNED_CONSENTS),
    where('preparerId', '==', preparerId),
    orderBy('signedAt', 'desc')
  );
  return onSnapshot(q, (snapshot) => {
    const consents = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(consents);
  });
};

// Subscribe to all signed consents for company (admin)
export const subscribeToCompanySignedConsents = (companyId, callback) => {
  const q = query(
    collection(db, SIGNED_CONSENTS),
    where('companyId', '==', companyId),
    orderBy('signedAt', 'desc')
  );
  return onSnapshot(q, (snapshot) => {
    const consents = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(consents);
  });
};

// Delete consent request
export const deleteConsentRequest = async (requestId) => {
  try {
    await deleteDoc(doc(db, CONSENT_REQUESTS, requestId));
    return true;
  } catch (error) {
    console.error('Error deleting consent request:', error);
    throw error;
  }
};

// Delete signed consent
export const deleteSignedConsent = async (consentId) => {
  try {
    await deleteDoc(doc(db, SIGNED_CONSENTS, consentId));
    return true;
  } catch (error) {
    console.error('Error deleting signed consent:', error);
    throw error;
  }
};

// Update signed consent
export const updateSignedConsent = async (consentId, data) => {
  try {
    const docRef = doc(db, SIGNED_CONSENTS, consentId);
    await updateDoc(docRef, {
      ...data,
      updatedAt: toEasternISOString()
    });
    return true;
  } catch (error) {
    console.error('Error updating signed consent:', error);
    throw error;
  }
};

// ==================== PDF DOCUMENT TEMPLATES (DocuSign-like) ====================

const DOCUMENT_TEMPLATES = 'documentTemplates';
const DOCUMENT_REQUESTS = 'documentRequests';
const SIGNED_DOCUMENTS = 'signedDocuments';

// Save PDF template
export const saveDocumentTemplate = async (templateData) => {
  try {
    // Extract preparerId and companyId from templateData
    const { preparerId, companyId, id, ...restData } = templateData;
    
    // Check if pdfData is too large (Firestore has 1MB limit per document)
    if (restData.pdfData && restData.pdfData.length > 900000) {
      console.warn('PDF data is very large:', restData.pdfData.length, 'bytes. May exceed Firestore limits.');
    }
    
    const dataToSave = {
      ...restData,
      companyId: companyId || null,
      preparerId: preparerId || null,
      createdAt: templateData.createdAt || toEasternISOString(),
      updatedAt: toEasternISOString()
    };
    
    // Remove undefined values
    Object.keys(dataToSave).forEach(key => {
      if (dataToSave[key] === undefined) {
        delete dataToSave[key];
      }
    });
    
    console.log('Saving template with keys:', Object.keys(dataToSave));
    console.log('PDF data size:', dataToSave.pdfData?.length || 0, 'characters');
    
    // If template has an existing Firestore ID (not temp id), update it
    if (id && !id.startsWith('template_')) {
      const docRef = doc(db, DOCUMENT_TEMPLATES, id);
      await updateDoc(docRef, dataToSave);
      console.log('Template updated:', id);
      return { id, ...dataToSave };
    } else {
      // Create new document
      const docRef = await addDoc(collection(db, DOCUMENT_TEMPLATES), dataToSave);
      console.log('Template created:', docRef.id);
      return { id: docRef.id, ...dataToSave };
    }
  } catch (error) {
    console.error('Error saving document template:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    throw error;
  }
};

// Get templates for preparer
export const getDocumentTemplates = async (preparerId, companyId) => {
  try {
    let q;
    if (preparerId) {
      q = query(collection(db, DOCUMENT_TEMPLATES), where('preparerId', '==', preparerId));
    } else if (companyId) {
      q = query(collection(db, DOCUMENT_TEMPLATES), where('companyId', '==', companyId));
    } else {
      return [];
    }
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting document templates:', error);
    return [];
  }
};

// Subscribe to templates
export const subscribeToDocumentTemplates = (preparerId, callback) => {
  console.log('Subscribing to document templates for preparerId:', preparerId);
  
  if (!preparerId) {
    console.warn('No preparerId provided, returning empty subscription');
    callback([]);
    return () => {};
  }
  
  const q = query(
    collection(db, DOCUMENT_TEMPLATES),
    where('preparerId', '==', preparerId)
  );
  return onSnapshot(q, (snapshot) => {
    const templates = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    console.log('Document templates received:', templates.length, 'templates');
    callback(templates);
  }, (error) => {
    console.error('Error subscribing to document templates:', error);
    callback([]); // Return empty array on error
  });
};

// Delete template
export const deleteDocumentTemplate = async (templateId) => {
  try {
    await deleteDoc(doc(db, DOCUMENT_TEMPLATES, templateId));
    return true;
  } catch (error) {
    console.error('Error deleting template:', error);
    throw error;
  }
};

// Get single template by ID
export const getDocumentTemplateById = async (templateId) => {
  try {
    const docRef = doc(db, DOCUMENT_TEMPLATES, templateId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting document template:', error);
    return null;
  }
};

// Create document request (send to client)
export const createDocumentRequest = async (requestData) => {
  try {
    const cleanedData = cleanObject({
      ...requestData,
      status: 'pending',
      createdAt: toEasternISOString(),
      expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() // 30 days
    });
    
    const docRef = await addDoc(collection(db, DOCUMENT_REQUESTS), cleanedData);
    return { id: docRef.id, ...cleanedData };
  } catch (error) {
    console.error('Error creating document request:', error);
    throw error;
  }
};

// Get document request (for client filling)
export const getDocumentRequest = async (requestId) => {
  try {
    const docRef = doc(db, DOCUMENT_REQUESTS, requestId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting document request:', error);
    return null;
  }
};

// Submit signed document
export const submitSignedDocument = async (requestId, signedData) => {
  try {
    // Get the original request
    const request = await getDocumentRequest(requestId);
    if (!request) throw new Error('Document request not found');

    // Create signed document record
    const signedDocData = cleanObject({
      requestId,
      templateId: request.templateId,
      templateName: request.template?.name || 'Document',
      companyId: request.companyId || null,
      preparerId: request.preparerId || null,
      clientName: signedData.clientInfo?.name || request.clientName || '',
      clientEmail: signedData.clientInfo?.email || request.clientEmail || '',
      fieldValues: signedData.fieldValues,
      signatures: signedData.signatures,
      pdfData: request.template?.pdfData,
      signedAt: toEasternISOString()
    });

    const docRef = await addDoc(collection(db, SIGNED_DOCUMENTS), signedDocData);

    // Update request status
    await updateDoc(doc(db, DOCUMENT_REQUESTS, requestId), {
      status: 'signed',
      signedAt: toEasternISOString(),
      signedDocumentId: docRef.id
    });

    return { id: docRef.id, ...signedDocData };
  } catch (error) {
    console.error('Error submitting signed document:', error);
    throw error;
  }
};

// Subscribe to document requests for preparer
export const subscribeToDocumentRequests = (preparerId, callback) => {
  const q = query(
    collection(db, DOCUMENT_REQUESTS),
    where('preparerId', '==', preparerId)
  );
  return onSnapshot(q, (snapshot) => {
    const requests = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(requests);
  }, (error) => {
    console.error('Error subscribing to document requests:', error);
    callback([]);
  });
};

// Subscribe to signed documents for preparer
export const subscribeToSignedDocuments = (preparerId, callback) => {
  const q = query(
    collection(db, SIGNED_DOCUMENTS),
    where('preparerId', '==', preparerId)
  );
  return onSnapshot(q, (snapshot) => {
    const documents = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(documents);
  }, (error) => {
    console.error('Error subscribing to signed documents:', error);
    callback([]);
  });
};

// Get signed document by ID
export const getSignedDocumentById = async (documentId) => {
  try {
    const docRef = doc(db, SIGNED_DOCUMENTS, documentId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting signed document:', error);
    return null;
  }
};

// Delete document request
export const deleteDocumentRequest = async (requestId) => {
  try {
    await deleteDoc(doc(db, DOCUMENT_REQUESTS, requestId));
    return true;
  } catch (error) {
    console.error('Error deleting document request:', error);
    throw error;
  }
};

// ==================== IRS WAIVER SUBMISSIONS ====================
const IRS_WAIVER_SUBMISSIONS = 'irsWaiverSubmissions';

// Save IRS Waiver form submission (pending verification)
export const saveIRSWaiverSubmission = async (submissionData) => {
  try {
    const cleanedData = cleanObject({
      ...submissionData,
      status: 'pending_verification',
      isPublished: false, // Only visible to assigned preparer and admin until published
      submittedAt: toEasternISOString(),
      verifiedAt: null,
      verifiedBy: null
    });
    
    const docRef = await addDoc(collection(db, IRS_WAIVER_SUBMISSIONS), cleanedData);
    console.log('IRS Waiver submission saved:', docRef.id);
    return { id: docRef.id, ...cleanedData };
  } catch (error) {
    console.error('Error saving IRS waiver submission:', error);
    throw error;
  }
};

// Subscribe to IRS Waiver submissions
// For admin: sees all submissions for their company
// For preparer: sees only their own submissions OR published submissions OR unassigned pending submissions
export const subscribeToIRSWaiverSubmissions = (preparerId, isAdmin, callback, companyId = null) => {
  try {
    // Query all submissions ordered by date
    const q = query(
      collection(db, IRS_WAIVER_SUBMISSIONS),
      orderBy('submittedAt', 'desc')
    );
    
    return onSnapshot(q, (snapshot) => {
      let submissions = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // First filter by company if companyId is provided (both admin and preparer)
      if (companyId) {
        submissions = submissions.filter(s => s.companyId === companyId);
      }
      
      // If not admin, further filter to show only:
      // 1. Submissions assigned to this preparer (by preparerId)
      // 2. Submissions completed by this preparer (by completedById)
      // 3. Submissions that are published (visible to all preparers in the company)
      // 4. Pending submissions with no assigned preparer (can be picked up by any preparer)
      // Note: Completed documents are only visible to the preparer who completed them or admin
      if (!isAdmin && preparerId) {
        submissions = submissions.filter(s => {
          // Always show if assigned to this preparer
          if (s.preparerId === preparerId) return true;
          // Always show if completed by this preparer
          if (s.preparerSection?.completedById === preparerId) return true;
          // Show published items only if NOT completed (completed items are private to the preparer who completed them)
          if (s.isPublished === true && s.status !== 'completed') return true;
          // Show pending submissions with no assigned preparer (any preparer can pick them up)
          if ((!s.preparerId || s.preparerId === '') && s.status === 'pending_verification') return true;
          return false;
        });
      }
      
      console.log('IRS Waiver Submissions loaded:', submissions.length, 'for preparerId:', preparerId, 'companyId:', companyId, 'isAdmin:', isAdmin);
      callback(submissions);
    });
  } catch (error) {
    console.error('Error subscribing to IRS waiver submissions:', error);
    return () => {};
  }
};

// Publish IRS Waiver submission (makes it visible to all preparers)
export const publishIRSWaiverSubmission = async (submissionId) => {
  try {
    const docRef = doc(db, IRS_WAIVER_SUBMISSIONS, submissionId);
    await updateDoc(docRef, {
      isPublished: true,
      publishedAt: toEasternISOString()
    });
    return true;
  } catch (error) {
    console.error('Error publishing IRS waiver submission:', error);
    throw error;
  }
};

// Unpublish IRS Waiver submission (makes it visible only to assigned preparer and admin)
export const unpublishIRSWaiverSubmission = async (submissionId) => {
  try {
    const docRef = doc(db, IRS_WAIVER_SUBMISSIONS, submissionId);
    await updateDoc(docRef, {
      isPublished: false,
      publishedAt: null
    });
    return true;
  } catch (error) {
    console.error('Error unpublishing IRS waiver submission:', error);
    throw error;
  }
};

// Verify/Approve IRS Waiver submission
export const verifyIRSWaiverSubmission = async (submissionId, preparerId, preparerName) => {
  try {
    const docRef = doc(db, IRS_WAIVER_SUBMISSIONS, submissionId);
    await updateDoc(docRef, {
      status: 'verified',
      verifiedAt: toEasternISOString(),
      verifiedBy: preparerId,
      verifiedByName: preparerName
    });
    return true;
  } catch (error) {
    console.error('Error verifying IRS waiver submission:', error);
    throw error;
  }
};

// Complete IRS Waiver submission (after preparer fills their section)
export const completeIRSWaiverSubmission = async (submissionId, preparerData) => {
  try {
    const docRef = doc(db, IRS_WAIVER_SUBMISSIONS, submissionId);
    await updateDoc(docRef, {
      status: 'completed',
      completedAt: toEasternISOString(),
      preparerSection: preparerData
    });
    return true;
  } catch (error) {
    console.error('Error completing IRS waiver submission:', error);
    throw error;
  }
};

// Update preparer section of IRS Waiver submission
export const updateIRSWaiverPreparerSection = async (submissionId, preparerSectionData) => {
  try {
    const docRef = doc(db, IRS_WAIVER_SUBMISSIONS, submissionId);
    await updateDoc(docRef, {
      preparerSection: preparerSectionData,
      preparerSectionUpdatedAt: toEasternISOString()
    });
    return true;
  } catch (error) {
    console.error('Error updating preparer section:', error);
    throw error;
  }
};

// Delete IRS Waiver submission
export const deleteIRSWaiverSubmission = async (submissionId) => {
  try {
    await deleteDoc(doc(db, IRS_WAIVER_SUBMISSIONS, submissionId));
    return true;
  } catch (error) {
    console.error('Error deleting IRS waiver submission:', error);
    throw error;
  }
};

// Update IRS Waiver submission (edit taxpayer info, tax year, etc.)
export const updateIRSWaiverSubmission = async (submissionId, data) => {
  try {
    const docRef = doc(db, IRS_WAIVER_SUBMISSIONS, submissionId);
    await updateDoc(docRef, {
      ...data,
      updatedAt: toEasternISOString()
    });
    return true;
  } catch (error) {
    console.error('Error updating IRS waiver submission:', error);
    throw error;
  }
};

// Get IRS Waiver submission by ID
export const getIRSWaiverSubmission = async (submissionId) => {
  try {
    const docRef = doc(db, IRS_WAIVER_SUBMISSIONS, submissionId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting IRS waiver submission:', error);
    return null;
  }
};

// ==================== MOBILE SIGNATURE SESSIONS ====================
const MOBILE_SIGNATURE_SESSIONS = 'mobileSignatureSessions';

// Create a mobile signature session for QR code signing
export const createMobileSignatureSession = async (sessionData) => {
  try {
    const sessionId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    const docRef = doc(db, MOBILE_SIGNATURE_SESSIONS, sessionId);
    
    const session = {
      sessionId,
      signatureType: sessionData.signatureType || 'taxpayer', // 'taxpayer' or 'spouse'
      companyId: sessionData.companyId || '',
      preparerId: sessionData.preparerId || '',
      companyName: sessionData.companyName || '',
      taxpayerName: sessionData.taxpayerName || '',
      spouseName: sessionData.spouseName || '',
      signerName: sessionData.signerName || sessionData.taxpayerName || '',
      status: 'pending', // 'pending', 'signed', 'expired'
      signature: null,
      signedAt: null,
      createdAt: toEasternISOString(),
      expiresAt: new Date(Date.now() + 30 * 60 * 1000).toISOString() // 30 minutes
    };
    
    await setDoc(docRef, session);
    return session;
  } catch (error) {
    console.error('Error creating mobile signature session:', error);
    throw error;
  }
};

// Get mobile signature session by ID
export const getMobileSignatureSession = async (sessionId) => {
  try {
    const docRef = doc(db, MOBILE_SIGNATURE_SESSIONS, sessionId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting mobile signature session:', error);
    return null;
  }
};

// Subscribe to mobile signature session (for real-time updates on desktop)
export const subscribeToMobileSignatureSession = (sessionId, callback) => {
  try {
    console.log('🔔 Setting up subscription for session:', sessionId);
    const docRef = doc(db, MOBILE_SIGNATURE_SESSIONS, sessionId);
    return onSnapshot(docRef, (docSnap) => {
      console.log('📥 Snapshot received, exists:', docSnap.exists());
      if (docSnap.exists()) {
        const data = { id: docSnap.id, ...docSnap.data() };
        console.log('📦 Session data:', JSON.stringify({ status: data.status, hasSignature: !!data.signature, signatureLength: data.signature?.length || 0 }));
        callback(data);
      } else {
        console.log('⚠️ Document does not exist or was deleted');
        callback(null);
      }
    }, (error) => {
      console.error('❌ Snapshot error:', error);
    });
  } catch (error) {
    console.error('Error subscribing to mobile signature session:', error);
    return () => {};
  }
};

// Update mobile signature session with signature
export const updateMobileSignatureSession = async (sessionId, signatureData) => {
  try {
    console.log('🔄 Updating session:', sessionId, 'with signature length:', signatureData?.length);
    const docRef = doc(db, MOBILE_SIGNATURE_SESSIONS, sessionId);
    
    // First check if document exists
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      console.error('❌ Session document does not exist:', sessionId);
      throw new Error('Session not found');
    }
    console.log('✅ Document exists, updating...');
    
    await updateDoc(docRef, {
      signature: signatureData,
      status: 'signed',
      signedAt: toEasternISOString()
    });
    console.log('✅ Firestore update completed successfully');
    return true;
  } catch (error) {
    console.error('❌ Error updating mobile signature session:', error.message, error.code);
    throw error;
  }
};

// Delete/expire mobile signature session
export const deleteMobileSignatureSession = async (sessionId) => {
  try {
    await deleteDoc(doc(db, MOBILE_SIGNATURE_SESSIONS, sessionId));
    return true;
  } catch (error) {
    console.error('Error deleting mobile signature session:', error);
    throw error;
  }
};

// ==================== TAX RETURNS ====================
const TAX_RETURNS = 'taxReturns';

// Add a tax return entry
export const addTaxReturn = async (taxReturnData) => {
  try {
    console.log('Adding tax return:', taxReturnData);
    const docRef = await addDoc(collection(db, TAX_RETURNS), {
      ...taxReturnData,
      createdAt: toEasternISOString()
    });
    console.log('Tax return added with ID:', docRef.id);
    return { id: docRef.id, ...taxReturnData };
  } catch (error) {
    console.error('Error adding tax return:', error);
    throw error;
  }
};

// Update a tax return entry
export const updateTaxReturn = async (id, data) => {
  try {
    await updateDoc(doc(db, TAX_RETURNS, id), {
      ...data,
      updatedAt: toEasternISOString()
    });
    return true;
  } catch (error) {
    console.error('Error updating tax return:', error);
    throw error;
  }
};

// Delete a tax return entry
export const deleteTaxReturn = async (id) => {
  try {
    await deleteDoc(doc(db, TAX_RETURNS, id));
    return true;
  } catch (error) {
    console.error('Error deleting tax return:', error);
    throw error;
  }
};

// Subscribe to tax returns for a preparer
export const subscribeToTaxReturns = (preparerId, callback) => {
  const q = query(
    collection(db, TAX_RETURNS),
    where('preparerId', '==', preparerId)
  );
  
  return onSnapshot(q, (snapshot) => {
    const taxReturns = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    // Sort by createdAt in memory
    taxReturns.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
    callback(taxReturns);
  }, (error) => {
    console.error('Error subscribing to tax returns:', error);
    callback([]);
  });
};

// Subscribe to tax returns for a company (all preparers)
export const subscribeToCompanyTaxReturns = (companyId, callback) => {
  const q = query(
    collection(db, TAX_RETURNS),
    where('companyId', '==', companyId)
  );
  
  return onSnapshot(q, (snapshot) => {
    const taxReturns = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    // Sort by createdAt in memory
    taxReturns.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
    callback(taxReturns);
  }, (error) => {
    console.error('Error subscribing to company tax returns:', error);
    callback([]);
  });
};
